import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentProcessor = ({ account, onClose, onProcessPayment }) => {
  const [paymentType, setPaymentType] = useState('patient');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const paymentTypeOptions = [
    { value: 'patient', label: 'Patient Payment' },
    { value: 'insurance', label: 'Insurance Payment' },
    { value: 'adjustment', label: 'Adjustment' },
    { value: 'writeoff', label: 'Write-off' }
  ];

  const paymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'check', label: 'Check' },
    { value: 'credit', label: 'Credit Card' },
    { value: 'debit', label: 'Debit Card' },
    { value: 'eft', label: 'Electronic Transfer' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsProcessing(true);

    setTimeout(() => {
      onProcessPayment({
        accountId: account?.id,
        paymentType,
        amount: parseFloat(amount),
        paymentMethod,
        referenceNumber,
        notes,
        timestamp: new Date()?.toISOString()
      });
      setIsProcessing(false);
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-background p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Process Payment</h2>
            <p className="text-sm text-muted-foreground mt-1">{account?.patientName} - Account #{account?.accountNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg border border-border">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Current Balance</p>
              <p className="text-lg font-bold text-foreground">${account?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Insurance Balance</p>
              <p className="text-lg font-bold text-foreground">${account?.insuranceBalance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>

          <Select
            label="Payment Type"
            required
            options={paymentTypeOptions}
            value={paymentType}
            onChange={setPaymentType}
          />

          <Input
            label="Payment Amount"
            type="number"
            required
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e?.target?.value)}
            description="Enter the payment amount in USD"
          />

          <Select
            label="Payment Method"
            required
            options={paymentMethodOptions}
            value={paymentMethod}
            onChange={setPaymentMethod}
          />

          <Input
            label="Reference Number"
            type="text"
            placeholder="Check number, transaction ID, etc."
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e?.target?.value)}
            description="Optional reference for tracking"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Add any additional notes about this payment..."
              rows={4}
              className="w-full px-4 py-3 bg-muted border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg border border-primary/20">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0" />
            <p className="text-sm text-primary">
              This transaction will be logged in the audit trail and cannot be deleted. Adjustments can be made through the adjustment process.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isProcessing}
              iconName="Check"
              iconPosition="left"
            >
              Process Payment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentProcessor;